/**
 * Google Sign-In for web (GIS) and Capacitor Android/iOS.
 *
 * Root cause (web works, app fails):
 * - Web uses Google Identity Services + Web client ID only.
 * - Native Credential Manager ALSO requires an Android OAuth client in Google Cloud
 *   with package com.edgefinances.app + the APK’s SHA-1. Without that, account
 *   pick fails (error 28444) even though the same Web client ID works in Chrome.
 *
 * Fix: try native Capgo Social Login first; on SHA-1 / Credential Manager failure,
 * fall back to Chrome Custom Tabs OAuth (Web client + redirect) which matches web.
 */
import { Capacitor } from '@capacitor/core';
import { API_BASE_URL } from '../api/client';
import { isIosLoginOnlyApp } from './platform';

const ENV_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

/** Must match Google Cloud → Web client → Authorized redirect URIs. */
export const GOOGLE_OAUTH_REDIRECT_URI = 'https://edgefinancepro.com/oauth/google.html';

/** Deep link that returns the id_token from the OAuth callback page into the app. */
export const GOOGLE_OAUTH_APP_LINK = 'com.edgefinances.app://google-auth';

/** Release keystore SHA-1 (Play upload / local release builds). */
export const ANDROID_RELEASE_SHA1 = '98:20:FD:34:39:01:D4:F4:FF:3D:C5:C8:28:0C:99:BB:50:B3:DD:34';
/** Debug keystore SHA-1 (local debug APKs). */
export const ANDROID_DEBUG_SHA1 = '0F:41:84:35:FC:FE:56:F6:00:63:6D:75:A2:68:E8:17:0A:32:29:7B';

let cachedClientId: string | null = ENV_CLIENT_ID || null;
let configPromise: Promise<string | null> | null = null;
let nativeInitClientId: string | null = null;
let nativePreinitStarted = false;

/** Web client id from build env or backend (for OTA / Netlify builds). */
export async function resolveGoogleClientId(): Promise<string | null> {
  if (cachedClientId) return cachedClientId;
  if (!configPromise) {
    configPromise = fetch(`${API_BASE_URL}/auth/google-config`, { credentials: 'omit' })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = (await res.json()) as { clientId?: string | null };
        const id = (data.clientId || '').trim();
        cachedClientId = id || null;
        return cachedClientId;
      })
      .catch(() => null);
  }
  return configPromise;
}

export function isGoogleAuthConfigured(): boolean {
  return Boolean((cachedClientId || ENV_CLIENT_ID || '').trim());
}

export function shouldUseNativeGoogleSignIn(): boolean {
  if (isIosLoginOnlyApp()) return false;
  return typeof window !== 'undefined' && Capacitor.isNativePlatform();
}

function extractIdToken(login: unknown): string | null {
  if (!login || typeof login !== 'object') return null;
  const root = login as Record<string, unknown>;
  const result = (root.result && typeof root.result === 'object' ? root.result : root) as Record<
    string,
    unknown
  >;
  const candidates = [result.idToken, result.id_token, root.idToken, root.id_token];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 20) return c.trim();
  }
  return null;
}

function extractIdTokenFromAppUrl(url: string): string | null {
  const hashIdx = url.indexOf('#');
  const queryIdx = url.indexOf('?');
  const hash = hashIdx >= 0 ? url.slice(hashIdx + 1) : '';
  const query =
    queryIdx >= 0 ? url.slice(queryIdx + 1, hashIdx >= 0 ? hashIdx : undefined) : '';
  for (const part of [hash, query]) {
    if (!part) continue;
    try {
      const id = new URLSearchParams(part).get('id_token');
      if (id && id.length > 20) return id;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function isCredentialManagerSetupError(err: unknown): boolean {
  const raw = err instanceof Error ? err.message : String(err || '');
  const lower = raw.toLowerCase();
  return (
    lower.includes('28444') ||
    lower.includes('developer console') ||
    lower.includes('not set up') ||
    lower.includes('did not return a sign-in token') ||
    lower.includes('sha-1') ||
    lower.includes('sha1')
  );
}

function friendlyGoogleError(err: unknown): Error {
  const raw = err instanceof Error ? err.message : String(err || '');
  const lower = raw.toLowerCase();
  if (isCredentialManagerSetupError(err)) {
    return new Error(
      'Google Sign-In needs an Android OAuth client in Google Cloud (package com.edgefinances.app + SHA-1). ' +
        `Add SHA-1 ${ANDROID_RELEASE_SHA1} for release / Play builds` +
        (Capacitor.getPlatform() === 'android' ? ` (debug: ${ANDROID_DEBUG_SHA1})` : '') +
        '. Also add redirect URI ' +
        GOOGLE_OAUTH_REDIRECT_URI +
        ' on the Web client. Trying browser sign-in…'
    );
  }
  if (lower.includes('cancel') || lower.includes('user_cancelled') || lower.includes('12501')) {
    return new Error('Google sign-in was cancelled.');
  }
  if (lower.includes('network') || lower.includes('timeout')) {
    return new Error('Network error during Google sign-in. Check your connection and try again.');
  }
  return new Error(raw || 'Google sign-in failed.');
}

function randomNonce(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Warm up Capgo Google provider as soon as the native shell boots. */
export async function preinitNativeGoogleSignIn(): Promise<void> {
  if (isIosLoginOnlyApp() || !shouldUseNativeGoogleSignIn() || nativePreinitStarted) return;
  nativePreinitStarted = true;
  const clientId = await resolveGoogleClientId();
  if (!clientId) return;
  try {
    const { SocialLogin } = await import('@capgo/capacitor-social-login');
    await SocialLogin.initialize({
      google: {
        webClientId: clientId,
        mode: 'online',
      },
    });
    nativeInitClientId = clientId;
  } catch {
    /* will retry on button press */
  }
}

async function ensureNativeInit(clientId: string): Promise<void> {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  if (nativeInitClientId !== clientId) {
    await SocialLogin.initialize({
      google: {
        webClientId: clientId,
        mode: 'online',
      },
    });
    nativeInitClientId = clientId;
  }
}

/** Capgo Credential Manager path (requires Android OAuth client + SHA-1). */
export async function signInWithGoogleNative(clientId: string): Promise<string> {
  await ensureNativeInit(clientId);
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  try {
    const login = await SocialLogin.login({
      provider: 'google',
      options: {
        filterByAuthorizedAccounts: false,
        autoSelectEnabled: false,
        scopes: ['openid', 'email', 'profile'],
      },
    });
    const idToken = extractIdToken(login);
    if (!idToken) {
      throw new Error(
        'Google did not return a sign-in token. Ensure the Android OAuth client (package + SHA-1) is registered in Google Cloud.'
      );
    }
    return idToken;
  } catch (err) {
    throw friendlyGoogleError(err);
  }
}

/**
 * Chrome Custom Tabs OAuth using the Web client ID (same as website).
 * Requires redirect URI registered on the Web OAuth client.
 */
export async function signInWithGoogleBrowser(clientId: string): Promise<string> {
  const { Browser } = await import('@capacitor/browser');
  const { App } = await import('@capacitor/app');

  const nonce = randomNonce();
  const authUrl =
    'https://accounts.google.com/o/oauth2/v2/auth?' +
    new URLSearchParams({
      client_id: clientId,
      redirect_uri: GOOGLE_OAUTH_REDIRECT_URI,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
    }).toString();

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let removeUrl: { remove: () => Promise<void> } | null = null;
    let removeFinished: { remove: () => Promise<void> } | null = null;

    const finish = async (err?: Error, token?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try {
        await removeUrl?.remove();
      } catch {
        /* ignore */
      }
      try {
        await removeFinished?.remove();
      } catch {
        /* ignore */
      }
      try {
        await Browser.close();
      } catch {
        /* ignore */
      }
      if (token) resolve(token);
      else reject(err || new Error('Google sign-in failed.'));
    };

    const timer = window.setTimeout(() => {
      void finish(new Error('Google sign-in timed out. Please try again.'));
    }, 120_000);

    void App.addListener('appUrlOpen', ({ url }) => {
      if (!url || !url.includes('google-auth')) return;
      const idToken = extractIdTokenFromAppUrl(url);
      if (idToken) void finish(undefined, idToken);
      else void finish(new Error('Google did not return a sign-in token.'));
    }).then((handle) => {
      removeUrl = handle;
    });

    void Browser.addListener('browserFinished', () => {
      // User closed the tab without completing — wait briefly for deep link race.
      window.setTimeout(() => {
        if (!settled) void finish(new Error('Google sign-in was cancelled.'));
      }, 600);
    }).then((handle) => {
      removeFinished = handle;
    });

    void Browser.open({ url: authUrl, presentationStyle: 'popover' }).catch((err) => {
      void finish(err instanceof Error ? err : new Error('Could not open Google sign-in.'));
    });
  });
}

/**
 * Native app entry: Credential Manager first, then browser OAuth fallback
 * (so the app works even before Android SHA-1 is registered in Google Cloud).
 */
export async function signInWithGoogleForApp(clientId: string): Promise<string> {
  try {
    return await signInWithGoogleNative(clientId);
  } catch (nativeErr) {
    const msg = nativeErr instanceof Error ? nativeErr.message : String(nativeErr || '');
    if (/cancel|user_cancelled|12501/i.test(msg)) throw nativeErr;

    try {
      return await signInWithGoogleBrowser(clientId);
    } catch (browserErr) {
      if (isCredentialManagerSetupError(nativeErr)) throw friendlyGoogleError(nativeErr);
      throw browserErr instanceof Error ? browserErr : new Error('Google sign-in failed.');
    }
  }
}
